import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { motion } from 'framer-motion';
import { useSelector } from "react-redux"; 
import { usePracticeById } from '../../hooks/Practice/usePracticeById';
import { 
  UserPlus, 
  Mail, 
  User, 
  Send,
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useCognitoSignUp from "../../hooks/Auth/useCognitoSignUp";
import { RootState } from "../../redux/index";
import useCreateUser from "../../hooks/User/useCreateUser";

interface InviteFormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  sendWelcomeEmail: boolean;
  accessLevel: string;
  roles: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const IntakeCoordinatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedPractice } = useSelector((state: RootState) => state.practices);
  const { handleCreateUser, cognito, loading, error } = useCognitoSignUp();
  const { createUserDb, user, isLoading: userIsLoading } = useCreateUser();
  const { fetchPracticeDetails } = usePracticeById();

  // State management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submissionStep, setSubmissionStep] = useState('');
  const [formData, setFormData] = useState<InviteFormData>({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    sendWelcomeEmail: true,
    accessLevel: 'intake_coordinator',
    roles: 'intake_coordinator'
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Memoized configurations - REMOVED unused configurations
  // const workingHourOptions = useMemo(() => [...], []);
  // const accessLevels = useMemo(() => [...], []);

  // Get practice ID safely
  const practiceId = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      return userData?.["custom:id_practice"] || '';
    } catch {
      return '';
    }
  }, []);

  // Fetch practice details on mount
  useEffect(() => {
    if (practiceId && !selectedPractice) {
      fetchPracticeDetails(practiceId);
    }
  }, [practiceId, selectedPractice, fetchPracticeDetails]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Required field validations
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle input changes
  const handleInputChange = useCallback((field: keyof InviteFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for the field being updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

 
  // Wait for Cognito response with better error handling
  const waitForCognitoResponse = useCallback(async (maxRetries = 10, delay = 1000): Promise<string> => {
    let retries = 0;
    
    while (retries < maxRetries) {
      // Check multiple possible locations for userSub
      const userSub = cognito?.userSub || 
                     cognito?.user?.userSub || 
                     cognito?.UserSub ||
                     cognito?.data?.userSub ||
                     cognito?.response?.userSub;
      

      if (userSub) {
        return userSub;
      }

      if (error) {
        console.error('❌ Cognito error detected:', error);
        throw new Error(`Cognito signup failed: ${error}`);
      }

      // If still loading, wait a bit more
      if (loading && retries < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        retries++;
        continue;
      }

      // If not loading and no userSub, something went wrong
      if (!loading && !userSub && retries > 3) {
        throw new Error('Failed to get user ID from Cognito signup. The account may have been created but the response was not received.');
      }

      await new Promise(resolve => setTimeout(resolve, delay));
      retries++;
    }

    throw new Error('Timeout waiting for Cognito signup response. Please check if the user was created and try again.');
  }, [cognito, loading, error]);

  // Create user in Cognito and database
  const createUserAccount = useCallback(async () => {
    if (!selectedPractice?._id) {
      throw new Error('Practice information is required');
    }

    try {
      // Step 1: Create Cognito user
      setSubmissionStep('Creating user account...');
      const userDataForCognito = {
        given_name: formData.firstName,
        family_name: formData.lastName,
        email: formData.email,
        role: 'intake_coordinator',
        status: 'pending',
        practice_id: selectedPractice._id,
        password: 'Password1234!'
      };

      
      // Clear any previous cognito state
      // Note: You might need to add a reset function to your useCognitoSignUp hook
      
      await handleCreateUser(userDataForCognito);
      
      // Step 2: Wait for Cognito response with improved logic
      setSubmissionStep('Verifying account creation...');
      const userSub = await waitForCognitoResponse();
      
      if (!userSub) {
        throw new Error('Failed to create user account - no user ID received');
      }

      // Step 3: Create user in database
      setSubmissionStep('Setting up user profile...');
      const userDataForDb = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        message: formData.message,
        sendWelcomeEmail: formData.sendWelcomeEmail,
        accessLevel: formData.accessLevel,
        roles: formData.roles,
        cognitoId: userSub,
        practiceId: selectedPractice._id,
      };

      await createUserDb(userDataForDb);
      
      // Step 4: Verify database user creation
      setSubmissionStep('Finalizing invitation...');
      
      // Wait a moment for the database operation to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return { cognitoId: userSub, userData: userDataForDb };
      
    } catch (error) {
      console.error('❌ Error in createUserAccount:', error);
      throw error;
    }
  }, [formData, selectedPractice, handleCreateUser, createUserDb, waitForCognitoResponse]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors
    
    try {
      await createUserAccount();
      
      setIsSuccess(true);
      
      // Auto redirect after 3 seconds
      setTimeout(() => {
        navigate('/practice/settings?tab=teams');
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error inviting coordinator:', error);
      
      // Determine user-friendly error message
      let errorMessage = 'Failed to send invitation. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('timeout') || error.message.includes('Timeout')) {
          errorMessage = 'The invitation is taking longer than expected. Please check if the user was created and try again if needed.';
        } else if (error.message.includes('email')) {
          errorMessage = 'There was an issue with the email address. Please verify it\'s correct and try again.';
        } else if (error.message.includes('Practice information')) {
          errorMessage = 'Practice information is missing. Please refresh the page and try again.';
        } else if (error.message.includes('Failed to create user account')) {
          errorMessage = 'Unable to create the user account. Please check your internet connection and try again.';
        } else if (error.message.includes('no user ID received')) {
          errorMessage = 'Account creation failed. Please try again or contact support if the problem persists.';
        } else {
          errorMessage = error.message;
        }
      }
        
      setErrors({ submit: errorMessage });
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
      setSubmissionStep('');
    }
  }, [validateForm, createUserAccount, navigate, errors]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      message: '',
      sendWelcomeEmail: true,
      accessLevel: 'intake_coordinator',
      roles: 'intake_coordinator'
    });
    setErrors({});
    setIsSuccess(false);
  }, []);

  // Success screen
  if (isSuccess) {
    return (
      <DashboardLayout>
        <div className="flex-1 p-8">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-lg p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Invitation Sent Successfully!
              </h2>
              <p className="text-gray-600 mb-6">
                An invitation has been sent to <strong>{formData.email}</strong>. 
                They will receive an email with instructions to join your team.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={resetForm}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Invite Another Coordinator
                </button>
                <button
                  onClick={() => navigate('/settings?tab=teams')}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  View Team
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex-1 p-4 md:p-8"
      >
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 rounded p-1"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <UserPlus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Invite Intake Coordinator</h1>
                <p className="text-gray-600">Send an invitation to join your team</p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <p className="text-red-700">{errors.submit}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center mb-6">
                <User className="w-5 h-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name * 
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                    aria-invalid={!!errors.firstName}
                    aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                  />
                  {errors.firstName && (
                    <p id="firstName-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                    aria-invalid={!!errors.lastName}
                    aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                  />
                  {errors.lastName && (
                    <p id="lastName-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>
            </div>

          
            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {submissionStep || 'Sending Invitation...'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default IntakeCoordinatorPage;
